import React, { useState } from "react";
import Head from "next/head";
import Footer from "../footer";
import Header from "../header";
import Image, { ImageLoaderProps } from 'next/image';
import StoneityButton from "@/components/stoneity-button/stoneityButton";
import { Swiper, SwiperSlide } from "swiper/react";
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useAppSelector } from "@/redux/app/hooks";
import { isTryDemoViewableRedux } from "@/redux/slices/user-context";
import { useTranslation } from "next-i18next";
import { Navigation } from "swiper";
import type { GetStaticProps, InferGetStaticPropsType } from "next";
import Promo from "../promo";
import Link from "next/link";
import styles from "@/styles/WorkManagement.module.css";
import "swiper/css";
import { IMAGE_DELIVERY, imageLoader } from "@/base/utils/dataHelper";
import { useRouter } from "next/router";

enum ProductFeaturesEnum {
  Project,
  Task,
  ProjectView,
  Form
}

const WorkMagamentHomePage = (_props: InferGetStaticPropsType<typeof getStaticProps>) => {
  const { t } = useTranslation('common');
  const router = useRouter();
  const isTryDemoViewable = useAppSelector(isTryDemoViewableRedux);
  const [productFeaturesTab, setProductFeaturesTab] = useState(ProductFeaturesEnum.Project)

  const isLanguage = router.locale?.toUpperCase().toString()

  const baseUrl = 'https://stoneity.com';
  const currentPath = router.asPath;
  const locale = router.locale || 'en';
  const canonicalUrl = locale === 'tr'
    ? `${baseUrl}/tr${currentPath}`
    : `${baseUrl}${currentPath}`;
  const alternateLocale = locale === 'tr' ? 'en' : 'tr';
  const alternatePath = `/work-management`;

  return (
    <>
      <Head>
        <title>{t('seo_work_management_title')}</title>
        <meta name="description" content={t('seo_work_management_description')} />
        <meta name="keywords" content="proje yönetimi, görev takibi, iş akışları, dinamik formlar, ekip yönetimi, Stoneity, project management, task tracking, workflows" />
        <link key="canonical" rel="canonical" href={canonicalUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:title" content={t('seo_work_management_title')} />
        <meta property="og:description" content={t('seo_work_management_description')} />
        <meta property="og:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta property="og:site_name" content="Stoneity" />
        <meta property="og:locale" content={locale === 'tr' ? 'tr_TR' : 'en_US'} />
        <meta property="og:locale:alternate" content={locale === 'tr' ? 'en_US' : 'tr_TR'} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={canonicalUrl} />
        <meta name="twitter:title" content={t('seo_work_management_title')} />
        <meta name="twitter:description" content={t('seo_work_management_description')} />
        <meta name="twitter:image" content={`${baseUrl}/stoneityLogo.png`} />
        <meta name="twitter:site" content="@stoneity" />

        {/* Alternate languages */}
        <link rel="alternate" hrefLang={locale} href={canonicalUrl} />
        <link rel="alternate" hrefLang={alternateLocale} href={`${baseUrl}/${alternateLocale}${alternatePath}`} />
        <link rel="alternate" hrefLang="x-default" href={`${baseUrl}${alternatePath}`} />
      </Head>
      <Header />
      <main className={styles.mainFlex}>
        <div className={styles.homeBannerWrapper}>
          <div className={styles.homeBannerSlogan}>
            <div className={styles.homeBannerText}>{t('withTitle')}</div>
            <div className={styles.homeBannerAnimationTextWrapper}>
              <ul className={styles.homeBannerAnimationTextContent}>
                <li className={styles.homeBannerAnimationTextItem}>{t('projectManagement')}</li>
                <li className={styles.homeBannerAnimationTextItem}>{t('mailManagement')}</li>
                <li className={styles.homeBannerAnimationTextItem}>{t('increaseProductivity')}</li>
                <li className={styles.homeBannerAnimationTextItem}>{t('reduceCosts')}</li>
              </ul>
            </div>
            {isTryDemoViewable &&
              <Link className={styles.link} href={'/try-demo'}>
                <StoneityButton
                  buttonText={t('tryItForFree')}
                  buttonStyle="dark"
                  width={200}
                  height={50}
                />
              </Link>
            }
            {isTryDemoViewable && <div className={styles.buttonInfoText}>{t('noCreditCard')}</div>}
          </div>
          <Image
            src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/d52cab92-4584-423f-79fa-6f29ddcce800` : `${IMAGE_DELIVERY}/648b3aa4-af50-4058-c645-fb15be340a00`}
            alt={""}
            style={{ height: 'auto', width: '100%', objectFit: 'contain', aspectRatio: 'auto', marginBottom: '-10px', background: '#D6EEF1' }}
            width={1920}
            height={470}
            loader={(p: ImageLoaderProps) => imageLoader(p.src, 1920, 470)}
          />
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.productFeaturesWrapper}>
            <div className={styles.productFeaturesSwiperWrapper}>
              <Swiper
                breakpoints={{
                  0: { slidesPerView: 1 },
                }}
                slidesPerView={4}
                className={styles.productFeaturesMySwiper}
                modules={[Navigation]}
                navigation={{
                  prevEl: "." + styles.productFeaturesSlideWrapper,
                  nextEl: "." + styles.productFeaturesSlideWrapper2,
                }}
              >
                <SwiperSlide>
                  <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.Project ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Project) }}>{t('projects')}</div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.Task ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Task) }}>{t('tasks')}</div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.ProjectView ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.ProjectView) }}>{t('projectView')}</div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.Form ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Form) }}>{t('dynamicForms')}</div>
                </SwiperSlide>
              </Swiper>
              <div className={styles.productFeaturesSlideWrapper}>
                <div className={styles.productFeaturesCustomLeftArrow}></div>
              </div>
              <div className={styles.productFeaturesSlideWrapper2}>
                <div className={styles.productFeaturesCustomRightArrow}></div>
              </div>
            </div>
            <div className={styles.productFeaturesTabWrapper}>
              <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.Project ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Project) }}>{t('projects')}</div>
              <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.Task ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Task) }}>{t('tasks')}</div>
              <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.ProjectView ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.ProjectView) }}>{t('projectView')}</div>
              <div className={`${styles.productFeaturesTab} ${productFeaturesTab === ProductFeaturesEnum.Form ? styles.productFeaturesTabActive : ''}`} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Form) }}>{t('dynamicForms')}</div>
            </div>
            <div className={`${styles.productFeaturesContentProject} ${productFeaturesTab === ProductFeaturesEnum.Project ? styles.productFeaturesContentProjectActive : ''}`}>
              <div className={styles.productFeaturesContentLeft}>
                {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                <div className={styles.text}>{t('projectTabExplain')}</div>
              </div>
              <Image
                className={styles.productFeaturesContentImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/aa65e7aa-2009-410b-fce0-c2d88f953200` : `${IMAGE_DELIVERY}/2976d982-35a8-46d0-0719-0a4c47d54a00`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
            </div>
            <div className={`${styles.productFeaturesContentTask} ${productFeaturesTab === ProductFeaturesEnum.Task ? styles.productFeaturesContentTaskActive : ''}`}>
              <div className={styles.productFeaturesContentLeft}>
                {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                <div className={styles.text}>{t('taskTabExplain')}</div>
              </div>
              <Image
                className={styles.productFeaturesContentImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/219baf37-8d1d-436b-ef92-6e6852268f00` : `${IMAGE_DELIVERY}/7ef4683e-96c2-46eb-09cf-ad4d9032e000`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
            </div>
            <div className={`${styles.productFeaturesContentProjectView} ${productFeaturesTab === ProductFeaturesEnum.ProjectView ? styles.productFeaturesContentProjectViewActive : ''}`}>
              <div className={styles.productFeaturesContentLeft}>
                {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                <div className={styles.text}>{t('projectViewTabExplain')}</div>
              </div>
              <Image
                className={styles.productFeaturesContentImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/64a72bd5-ac4a-415f-fded-f46add25ba00` : `${IMAGE_DELIVERY}/3c876b02-655a-462a-bfe6-c76728fbf200`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
            </div>
            <div className={`${styles.productFeaturesContentForm} ${productFeaturesTab === ProductFeaturesEnum.Form ? styles.productFeaturesContentFormActive : ''}`}>
              <div className={styles.productFeaturesContentLeft}>
                {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                <div className={styles.text}>{t('dynamicFormsTabExplain')}</div>
              </div>
              <Image
                className={styles.productFeaturesContentImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/5fb10099-0f91-41e8-d421-49441cfe7100` : `${IMAGE_DELIVERY}/e8f9cad2-d697-44f5-3c50-c1c515a20a00`}
                alt=""
                width={750}
                height={500}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
              />
            </div>
          </div>
          <div className={styles.responsiveProductFeaturesWrapper}>
            <div className={styles.productFeaturesSwiperWrapper}>
              <Swiper
                breakpoints={{
                  0: { slidesPerView: 1 },
                }}
                slidesPerView={4}
                className={styles.productFeaturesMySwiper}
                modules={[Navigation]}
                navigation={{
                  prevEl: "." + styles.responsiveProductFeaturesSlideWrapper,
                  nextEl: "." + styles.responsiveProductFeaturesSlideWrapper2,
                }}
              >
                <SwiperSlide>
                  <div className={styles.responsiveProductFeaturesTab} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Project) }}>{t('projects')}</div>
                  <div className={styles.responsiveProductFeaturesContentProject}>
                    <div className={styles.productFeaturesContentLeft}>
                      {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                      <div className={styles.text}>{t('projectTabExplain')}</div>
                    </div>
                    <Image
                      className={styles.productFeaturesContentImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/aa65e7aa-2009-410b-fce0-c2d88f953200` : `${IMAGE_DELIVERY}/2976d982-35a8-46d0-0719-0a4c47d54a00`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={styles.responsiveProductFeaturesTab} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Task) }}>{t('tasks')}</div>
                  <div className={styles.responsiveProductFeaturesContentTask}>
                    <div className={styles.productFeaturesContentLeft}>
                      {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                      <div className={styles.text}>{t('taskTabExplain')}</div>
                    </div>
                    <Image
                      className={styles.productFeaturesContentImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/219baf37-8d1d-436b-ef92-6e6852268f00` : `${IMAGE_DELIVERY}/7ef4683e-96c2-46eb-09cf-ad4d9032e000`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={styles.responsiveProductFeaturesTab} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.ProjectView) }}>{t('projectView')}</div>
                  <div className={styles.responsiveProductFeaturesContentProjectView}>
                    <div className={styles.productFeaturesContentLeft}>
                      {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                      <div className={styles.text}>{t('projectViewTabExplain')}</div>
                    </div>
                    <Image
                      className={styles.productFeaturesContentImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/64a72bd5-ac4a-415f-fded-f46add25ba00` : `${IMAGE_DELIVERY}/3c876b02-655a-462a-bfe6-c76728fbf200`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                  </div>
                </SwiperSlide>
                <SwiperSlide>
                  <div className={styles.responsiveProductFeaturesTab} onClick={() => { setProductFeaturesTab(ProductFeaturesEnum.Form) }}>{t('dynamicForms')}</div>
                  <div className={styles.responsiveProductFeaturesContentForm}>
                    <div className={styles.productFeaturesContentLeft}>
                      {/* <div className={styles.title}>Get key insights without cobbling them together</div> */}
                      <div className={styles.text}>{t('dynamicFormsTabExplain')}</div>
                    </div>
                    <Image
                      className={styles.productFeaturesContentImg}
                      src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/5fb10099-0f91-41e8-d421-49441cfe7100` : `${IMAGE_DELIVERY}/e8f9cad2-d697-44f5-3c50-c1c515a20a00`}
                      alt=""
                      width={750}
                      height={500}
                      loader={(p: ImageLoaderProps) => imageLoader(p.src, 750, 500)}
                    />
                  </div>
                </SwiperSlide>
              </Swiper>
              <div className={styles.responsiveProductFeaturesSlideWrapper}>
                <div className={styles.responsiveProductFeaturesCustomLeftArrow}></div>
              </div>
              <div className={styles.responsiveProductFeaturesSlideWrapper2}>
                <div className={styles.responsiveProductFeaturesCustomRightArrow}></div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex} style={{ background: '#F6F6F6' }}>
          <div className={styles.teamWorkWrapper}>
            <div className={styles.teamWorkTextWrapper}>
              <div className={styles.title}>{t('teamsAreVeryPleasedToWorkWithStoneity')}</div>
              <div className={styles.text}>{t('teamsAreVeryPleasedToWorkWithStoneityExplain')}</div>
            </div>
            <Image
              src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/3582394e-834b-4e0c-a36b-ecf5bd4c2200` : `${IMAGE_DELIVERY}/3338fb6a-fd43-443b-ea6a-1cadeabaef00`}
              alt={""}
              className={styles.teamWorkImg}
              width={680}
              height={450}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 680, 450)}
            />
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.productCardWrapper}>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/1ff1e792-e195-4af6-9f5b-67d5f24edc00` : `${IMAGE_DELIVERY}/20f268e5-f918-4fa7-ca66-acdbc8884b00`}
                alt=""
                width={670}
                height={375}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t('interactiveProjectManagement')}</div>
                <div className={styles.text}>{t('interactiveProjectManagementExplain')}</div>
              </div>
            </div>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/d45c07cb-3a23-46e8-1317-60cf211fa400` : `${IMAGE_DELIVERY}/0c5f4b44-d427-44e9-0c59-d519b6b13200`}
                alt=""
                width={670}
                height={375}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t('teamKnowledgeRepository')}</div>
                <div className={styles.text}>{t('teamKnowledgeRepositoryExplain')}</div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex} style={{ background: '#F6F9FF', position: 'relative', overflow: 'hidden' }}>
          <div className={styles.sloganWrapper}>
            <Image
              className={styles.sloganImg}
              src={`${IMAGE_DELIVERY}/6f6443e3-f2e0-4d29-7980-f8c854c1a500`}
              alt=""
              width={190}
              height={310}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 190, 310)}
            />
            <div>
              <Image
                className={styles.sloganImgResponsive}
                src={`${IMAGE_DELIVERY}/c49fab41-4d44-4421-6f2c-9a9108ffc900`}
                alt=""
                width={670}
                height={170}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 170)}
              />
            </div>
            <div className={styles.sloganTextContent}>
              <div className={styles.title}>{t('teamProjectAndBusinessManagementPreference')} <span>{t('app_title')}</span></div>
              <div className={styles.buttonWrapper}>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle="dark"
                      buttonText={t('tryItForFree')}
                      width={200}
                      height={45}
                    />
                  </Link>
                }
                <Link className={styles.link} href={'/work-management/plan'}>
                  <StoneityButton
                    buttonStyle="light"
                    buttonText={t('viewPlan')}
                    width={200}
                    height={45}
                  />
                </Link>
              </div>
            </div>
            <Image
              className={`${styles.sloganImg} ${styles.sloganImgRight}`}
              src={`${IMAGE_DELIVERY}/ab389015-63aa-4c00-a478-0f2568621900`}
              alt=""
              width={190}
              height={310}
              loader={(p: ImageLoaderProps) => imageLoader(p.src, 190, 310)}
            />
            <div>
              <Image
                className={styles.sloganImgResponsive}
                src={`${IMAGE_DELIVERY}/0a0029a2-cdcd-41c6-ef0b-588eac191b00`}
                alt=""
                width={670}
                height={170}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 170)}
              />
            </div>
          </div>
        </div>
        <div className={styles.pageFlex}>
          <div className={styles.productCardWrapper}>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/cb5f64f6-056f-42bb-11a9-add44b558000` : `${IMAGE_DELIVERY}/aacaaec0-a674-441b-c672-d8a2b8a23200`}
                alt=""
                width={670}
                height={375}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t('formsAndRecordCreation')}</div>
                <div className={styles.text}>{t('formsAndRecordCreationExplain')}</div>
              </div>
            </div>
            <div className={styles.productCard}>
              <Image
                className={styles.productCardImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/c739de19-6e6a-40f7-e5ec-0f0dc215ad00` : `${IMAGE_DELIVERY}/82bdb0f1-4fde-4833-5c40-fba64ddc1300`}
                alt=""
                width={670}
                height={375}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 670, 375)}
              />
              <div className={styles.productCardTextContent}>
                <div className={styles.title}>{t('powerfulBusinessAutomations')}</div>
                <div className={styles.text}>{t('powerfulBusinessAutomationsExplain')}</div>
              </div>
            </div>
          </div>
        </div>
        <div className={styles.pageFlex} style={{ background: '#F8F4EA' }}>
          <div className={styles.goalAndReportWrapper}>
            <div className={styles.title}>{t('goalsAndReports')}</div>
            <div className={styles.goalAndReportRow}>
              <div className={styles.goalAndReportBox}>
                <div className={styles.title}>{t('trackProjectProgress')}</div>
                <div className={styles.text}>{t('trackProjectProgressExplain')}</div>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle="dark"
                      buttonText={t('tryItForFree')}
                      width={150}
                      height={40}
                      buttonClass="goalAndReportFreeButton"
                    />
                  </Link>
                }
              </div>
              <Image
                className={styles.goalAndReportFirstImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/f2128faf-97da-4893-5cb8-51d9e7428d00` : `${IMAGE_DELIVERY}/d59bcd47-96b6-4bc7-b02d-21642b34c500`}
                alt=""
                width={520}
                height={480}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 520, 480)}
              />
            </div>
            <div className={styles.goalAndReportRow}>
              <Image
                className={styles.goalAndReportSecondImg}
                src={isLanguage === "TR" ? `${IMAGE_DELIVERY}/27163c38-11af-44ab-b94a-c62b4a36c400` : `${IMAGE_DELIVERY}/c93627c3-a2e0-484d-956b-0a51b5545000`}
                alt=""
                width={450}
                height={450}
                loader={(p: ImageLoaderProps) => imageLoader(p.src, 450, 450)}
              />
              <div className={styles.goalAndReportBox}>
                <div className={styles.title}>{t('closelyMonitorCompanyGoals')}</div>
                <div className={styles.text}>{t('closelyMonitorCompanyGoalsExplain')}</div>
                {isTryDemoViewable &&
                  <Link className={styles.link} href={'/try-demo'}>
                    <StoneityButton
                      buttonStyle="dark"
                      buttonText={t('tryItForFree')}
                      width={150}
                      height={40}
                      buttonClass="goalAndReportFreeButton"
                    />
                  </Link>
                }
              </div>
            </div>
          </div>
        </div>
        <Promo product="work-management" />
      </main>
      <Footer />
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale || 'en', ['common'])),
  },
});

export default WorkMagamentHomePage;